/** @jsx jsx */
import * as React from 'react';
import { jsx } from "@emotion/core";
import { get, isEmpty, isNil, map } from 'lodash';
import * as styles from './style';
import { IAppState } from '../../../models/IAppState';
import { connect } from 'react-redux';
import moment = require('moment');
import AppUtils from '../../../utils/AppUtils';
import { UserFitnessActions } from '../../../actions/UserFitnessActions';
import FitnessTemplateWrapper, { FitnessTemplateStateType } from './FitnessTemplateWrapper/FitnessTemplateWrapper';
import { IUserFitnessManagedPlanParams } from '../../Patient/Transform/interface';
import { OngoingConsultationActions } from '../../../actions/OngoingConsultationActions';
import { IMicrocycle, IUserFitnessPlan } from '../../FitnessPlan/FitnessTemplate/interface';
import { toastNotificationAppearance } from '../../../utils/AppConstants';
import { CustomConfirmationModal } from '../../../components/FitnessPlanTemplate/MicrocycleTemplate/CustomConfirmationModal/CustomConfirmationModal';

interface IFitnessWODEditViewContProps {
    userId: string;
    userProfile: any;
    coachId: string;
    userFitnessPlan: IMicrocycle;
}

interface IFitnessPlanCard {
    title: string,
    workoutDays: number,
    restDays: number,
    state: string,
    dayFocusMapping: { id: string | number, day: string, focus: string }[]
}

interface IMapDispatchToProps {
    setUserFitnessPlan: (userPlan: any, startDate: string | number, isPlanPublished: boolean) => void;
    fetchUserManagedFitnessPlan: (requestParams: IUserFitnessManagedPlanParams) => Promise<IMicrocycle>;
    deleteUserFitnessPlan: (planId: number) => Promise<any>;
}

const FitnessWODEditViewCont = (props: IFitnessWODEditViewContProps & IMapDispatchToProps) => {
    const { userId, userProfile, setUserFitnessPlan, fetchUserManagedFitnessPlan, deleteUserFitnessPlan, userFitnessPlan } = props;

    const mondayOfTheWeek = AppUtils.getEpochInUTC(AppUtils.getStartOfTheWeekFromMonday(new Date()));

    // const [startDate, setStartDate] = React.useState(mondayOfTheWeek);
    // const [endDate, setEndDate] = React.useState(moment(mondayOfTheWeek).add(6, 'days').endOf('day').valueOf());
    const [showFitnessTemplate, setShowFitnessPlan] = React.useState<boolean>(false);
    const [templateType, setTemplateType] = React.useState<FitnessTemplateStateType>(null);
    const [fitnessPlan, setFitnessPlan] = React.useState<IMicrocycle>(null);
    const [fitnessPlanCardData, setFitnessPlanCardData] = React.useState<IFitnessPlanCard>(null);
    const [showDeleteModal, setDeleteModalDisplay] = React.useState(false);

    React.useEffect(() => {
        setFitnessPlan(userFitnessPlan);
        userFitnessPlan && prepareFitnessPlanCardData(userFitnessPlan);
    }, [userFitnessPlan]);

    React.useEffect(() => {
        fetchFitnessPlan();
    }, []);

    const fetchFitnessPlan = async () => {

        try {
            const queryParams = {
                userId,
                tenantId: 3,
                userPlanStartDate: mondayOfTheWeek
            }
            let userAssignedPlan: IMicrocycle = null;
            userAssignedPlan = await fetchUserManagedFitnessPlan(queryParams);
            setUserFitnessPlan(userAssignedPlan, mondayOfTheWeek, false); //Todo check for publishedPlan
        }
        catch {
            AppUtils.triggerToastNotification("Failed to get customer plan. Please try again.", toastNotificationAppearance.error);
        }
    }

    const prepareFitnessPlanCardData = (userAssignedPlan: IMicrocycle) => {

        const fitnessCardData: IFitnessPlanCard = {} as IFitnessPlanCard;
        fitnessCardData.title = userAssignedPlan.description;
        //let restDayCount = 0;
        const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        fitnessCardData.dayFocusMapping = [];
        map(userAssignedPlan.prewods, (wod: any, i: number) => {
            fitnessCardData.dayFocusMapping.push({ id: wod.id, day: weekDays[i], focus: wod.workoutType })
        })

        fitnessCardData.workoutDays = userAssignedPlan.workoutDays;
        fitnessCardData.restDays = 7 - userAssignedPlan.workoutDays;
        fitnessCardData.state = userAssignedPlan.state;
        setFitnessPlanCardData(fitnessCardData);
    }

    const createPlanHandler = () => {
        setUserFitnessPlan(null, mondayOfTheWeek, false);
        setShowFitnessPlan(true);
        setTemplateType(FitnessTemplateStateType.CREATE_PLAN)
    }

    const hideTemplate = () => {
        setShowFitnessPlan(false);
        setTemplateType(null);

    }

    const selectTemplateHandler = () => {
        setShowFitnessPlan(true);
        setTemplateType(FitnessTemplateStateType.SELECT_TEMPLATE)
    }

    const deleteUserPlan = () => {
        //setTransformFactorsData(prevState => ({ ...prevState, [PATIENT_TRANSFORM_FACTORS.FITNESS]: null }));
        setDeleteModalDisplay(false);
        deleteUserFitnessPlan(fitnessPlan.id)
            .then(() => {
                setUserFitnessPlan(null, null, null);
            })
            .catch(err => AppUtils.triggerToastNotification("Failed to delete assigned plan.", toastNotificationAppearance.error));
    }

    const handleEditPatientPlan = () => {
        setShowFitnessPlan(true);
        setTemplateType(FitnessTemplateStateType.EDIT_PLAN)
        //setUserFitnessPlan(fitnessPlan, fitnessPlan.userPlanStartDate, null);
        // const fitnessPlanId = isPublishedFitnessPlan ? get(fitnessPlan, "microCycleEntry.id", "") : get(fitnessPlan, "id", "");
        // RouteUtils.navigateToHrefUrl(null, RouteUtils.getEditUserFitnessPlanUrl(fitnessPlanId, userId));
    }


    return (
        <React.Fragment>
            <div css={styles.fitnessPlanCard}>
                <div css={styles.cardHeader}>
                    <div css={styles.cardTitle}>Fitness Daily WOD</div>
                </div>
                {(isEmpty(fitnessPlan) || isEmpty(fitnessPlanCardData)) ? <div css={styles.noPlanBody}>
                    <div css={styles.ctaWrapper}>
                        <div css={styles.cta} onClick={selectTemplateHandler}> <img src="/images/template.svg" /><span>Select a Template</span></div>
                        <div css={styles.cta} onClick={createPlanHandler}> <img src="/images/add.svg" /><span>Create New Plan</span></div>
                    </div>
                </div>
                    : <div css={styles.fitnessCard}>
                        <div css={styles.fitnessCardHeader}>
                            <img src="/images/excercise.svg" />
                            <div css={styles.fitnessCardTitle}>
                                <span >{fitnessPlanCardData.title}</span>
                                {fitnessPlanCardData.state === 'DRAFT' ? <span css={styles.fitnessPlanState}>(Draft)</span> : null}
                            </div>
                            <img css={styles.cardAction} src="/images/text-edit.svg" onClick={handleEditPatientPlan} />
                            <img css={styles.cardAction} src="/images/delete.svg" onClick={() => setDeleteModalDisplay(true)} />
                        </div>

                        <div css={styles.fitnessCardDaysRow}>
                            <span>Workout Days: <em>{fitnessPlanCardData.workoutDays}</em></span>
                            <span css={styles.seperator} />
                            <span>Rest Days: <em>{fitnessPlanCardData.restDays}</em> </span>
                            <span css={styles.seperator} />
                            <span>Repeats For All Weeks</span>
                        </div>

                        <div css={styles.fitnessDayFocusRow}>
                            {
                                map(fitnessPlanCardData.dayFocusMapping, (dayFocus: { id: string | number, day: string, focus: string }, i: number) => (
                                    <React.Fragment key={dayFocus.id}>
                                        <span >{dayFocus.day}: {dayFocus.focus || ' - '}</span>
                                        { i < 6 ? <span css={styles.seperator} /> : null}
                                    </React.Fragment>
                                ))
                            }
                        </div>
                    </div>
                }

                {showFitnessTemplate ? <FitnessTemplateWrapper onBackPress={hideTemplate} templateType={templateType} /> : null}
            </div>

            {showDeleteModal && <CustomConfirmationModal
                discardAction={() => setDeleteModalDisplay(false)}
                confirmAction={deleteUserPlan}
                modalText={{
                    header: "Confirm Delete?",
                    message: "Do you want to delete the assigned plan?",
                    discard: "Cancel",
                    confirm: "Delete",
                }}
                modalSize="xx-small"
            />}
        </React.Fragment>
    )
}


const mapStateToProps = (state: IAppState) => ({
    userId: get(state.patientCompleteInfo, "profile.curefitUserId", null),
    userProfile: get(state.patientCompleteInfo, "profile", null),
    coachId: get(state.accountData, "id", null),
    userFitnessPlan: get(state.ongoingConsultation.fitnessPlan, "userFitnessPlan", null)
});

const mapDispatchToProps = (dispatch: React.Dispatch<any>): IMapDispatchToProps => ({
    // fetchUserFitnessPlan: (requestParams: IUserFitnessPlanRequestParams) => UserFitnessActions.fetchUserFitnessPlan(requestParams),
    fetchUserManagedFitnessPlan: (requestParams: IUserFitnessManagedPlanParams) => OngoingConsultationActions.fetchUserFitnessManagedPlan(requestParams),
    setUserFitnessPlan: (userPlan: any, startDate: string | number, isPlanPublished: boolean) => OngoingConsultationActions.setFitnessPlan(userPlan, startDate, isPlanPublished, dispatch),
    deleteUserFitnessPlan: (planId: number) => UserFitnessActions.deleteUserFitnessPlan(planId)
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FitnessWODEditViewCont);


